import React, { useEffect, useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardHeader, Button, Table, Spinner,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { Can } from '../../utilities/Can';
import { get, getFile } from '../../helpers/api_helper';
import OrderImport from '../../components/ChannelAdvisor/OrderImport';

document.title = 'Settings Utilities';

const PRODUCT_FEED_SHARDS_ENDPOINT = '/api/openaicommerce/product-feed/shards';
const PRODUCT_FEED_EXPORT_ENDPOINT = '/api/openaicommerce/product-feed/';

function getFilenameFromHeaders(headers, fallback) {
  const disposition = headers['content-disposition'];
  const filenameMatch = disposition && disposition.match(/filename="?([^"]+)"?/i);

  return filenameMatch ? filenameMatch[1] : fallback;
}

function Utilities(props) {
  const { t } = props;
  const [shards, setShards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportingShard, setExportingShard] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const loadShards = async () => {
    try {
      setLoading(true);
      const result = await get(PRODUCT_FEED_SHARDS_ENDPOINT);

      setShards(result.shards || []);
    } catch (error) {
      toastr.error(t(`Could not load product feed shards: ${error.message}`), t('Error'));
    } finally {
      setLoading(false);
    }
  };

  const downloadBlob = (response, fallbackFilename) => {
    const filename = getFilenameFromHeaders(response.headers, fallbackFilename);
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const getShardExportUrl = (shard) => {
    if (Number(shard.shard_start) === 1) {
      return PRODUCT_FEED_EXPORT_ENDPOINT;
    }

    return `${PRODUCT_FEED_EXPORT_ENDPOINT}${shard.shard_start}`;
  };

  const exportShardFile = async (shard) => {
    try {
      setExportingShard(shard.shard_start);
      const response = await getFile(getShardExportUrl(shard));

      downloadBlob(response, shard.filename);
    } finally {
      setExportingShard(null);
    }
  };

  const downloadShard = async (shard, showSuccess = true) => {
    try {
      await exportShardFile(shard);

      if (showSuccess) {
        toastr.success(t('Product feed export downloaded'), t('Success'));
      }
    } catch (error) {
      toastr.error(t('Could not download product feed export'), t('Error'));
    }
  };

  const downloadAllShards = async () => {
    try {
      setExportingAll(true);
      let shardsToDownload = shards;

      if (shardsToDownload.length === 0) {
        const result = await get(PRODUCT_FEED_SHARDS_ENDPOINT);
        shardsToDownload = result.shards || [];
        setShards(shardsToDownload);
      }

      if (shardsToDownload.length === 0) {
        const response = await getFile(PRODUCT_FEED_EXPORT_ENDPOINT);

        downloadBlob(response, 'openai-products-shard-000000001.jsonl.gz');
        toastr.success(t('Product feed export downloaded'), t('Success'));
        return;
      }

      await shardsToDownload.reduce(async (previousDownload, shard) => {
        await previousDownload;
        await exportShardFile(shard);
      }, Promise.resolve());

      toastr.success(t('Product feed exports downloaded'), t('Success'));
    } catch (error) {
      toastr.error(t(`Could not download product feed exports: ${error.message}`), t('Error'));
    } finally {
      setExportingAll(false);
    }
  };

  useEffect(() => {
    loadShards();
  }, []);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xxl="8">
            <Can i="see" this="Settings">
              <OrderImport />
            </Can>
            <Card>
              <CardHeader className="bg-transparent border-bottom">{t('Utilities')}</CardHeader>
              <CardBody>
                <Can i="see" this="Settings">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">{t('Product Feed Export')}</h5>
                    <div>
                      <Button color="light" onClick={loadShards} disabled={loading || exportingAll} className="me-2">
                        {loading && <Spinner size="sm" className="me-1" />}
                        {t('Refresh')}
                      </Button>
                      <Button color="primary" onClick={downloadAllShards} disabled={loading || exportingAll || exportingShard !== null}>
                        {exportingAll && <Spinner size="sm" className="me-1" />}
                        <span className="mdi mdi-download-multiple me-1" />
                        {t('Export All Files')}
                      </Button>
                    </div>
                  </div>

                  <Table responsive bordered className="mb-0">
                    <thead>
                      <tr>
                        <th>{t('File')}</th>
                        <th>{t('Shard Start')}</th>
                        <th>{t('Shard End')}</th>
                        <th>{' '}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shards.map((shard) => (
                        <tr key={shard.shard_start}>
                          <td>{shard.filename}</td>
                          <td>{shard.shard_start}</td>
                          <td>{shard.shard_end}</td>
                          <td className="text-end">
                            <Button
                              color="primary"
                              className="btn-soft-primary"
                              onClick={() => downloadShard(shard)}
                              disabled={exportingShard !== null || exportingAll}
                            >
                              {exportingShard === shard.shard_start && <Spinner size="sm" className="me-1" />}
                              <span className="mdi mdi-download me-1" />
                              {t('Export File')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!loading && shards.length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">{t('No exports available')}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Can>

                <Can not i="see" this="Settings"><p>{t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Utilities.defaultProps = {
  t: (x) => (x),
};

Utilities.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Utilities));
